import capitalize from "lodash/capitalize";
import { useActivityPermissions } from "PFApp/activities/hooks";
import { useCurrentActivity } from "PFApp/activities/show/hooks/use_current_activity";
import { AddActivityProfilesButton } from "PFApp/activities/show/parts/activity_profiles/add_activity_profiles_button";
import { useActivityChat } from "PFApp/activities/show/parts/hooks/use_activity_chat";
import { ActivityProfilesAvatarStack } from "PFApp/components/activity_profiles_avatar_stack";
import { BecomeCoownerButton } from "PFApp/components/become_coowner_button/become_coowner_button";
import { useAssignee, useOwners, useReviewers } from "PFApp/hooks/activity_commons/participants";
import { Inline, Stack } from "PFComponents/containers";
import { Typography } from "PFComponents/typography";
import { Profile } from "PFTypes";
import { useTranslation } from "react-i18next";

import css from "./activity_profiles_section.module.scss";

type LabelProps = {
  translationKey: string;
  profiles: Profile[];
};

const Label = ({ translationKey, profiles }: LabelProps) => {
  const { t } = useTranslation("activities");

  return (
    <Typography variant="bodyRegular" className={css.label}>
      {translationKey &&
        `${t(`show.parts.${translationKey}` as unknown as TemplateStringsArray, {
          count: profiles.length
        })}`}
    </Typography>
  );
};

export const ActivityProfilesSection = () => {
  const { t } = useTranslation("activities", { keyPrefix: "show.parts" });
  const activity = useCurrentActivity();
  const { startChat } = useActivityChat();
  const { canBecomeCoOwner } = useActivityPermissions(activity);

  const { owners, principalOwner, canView: canViewOwners } = useOwners(activity);
  const { assignee, canView: canViewAssignee } = useAssignee(activity);
  const { reviewers, canView: canViewReviewers } = useReviewers(activity);

  const isDraft = activity.state === "draft";

  const rowsData: (LabelProps & {
    show: boolean;
  })[] = [
    {
      translationKey: "principalOwner",
      profiles: [principalOwner!],
      show: canViewOwners && !!principalOwner
    },
    {
      translationKey: "owner",
      profiles: owners,
      show: canViewOwners
    },
    {
      translationKey: "assignee",
      profiles: [assignee!],
      show: canViewAssignee
    },
    {
      translationKey: "reviewer",
      profiles: reviewers,
      show: canViewReviewers
    }
  ].filter(({ show }) => show);

  return (
    <Stack qaId="ActivitySummary-Profiles">
      <Inline alignItems="center" spread>
        <Typography variant="bodyBold">{t("participants")}</Typography>
        {canBecomeCoOwner && <BecomeCoownerButton activity={activity} small />}
        <AddActivityProfilesButton small />
      </Inline>
      {rowsData.map(({ show, translationKey, profiles }) => (
        <Inline key={`row-${translationKey}`}>
          <Label translationKey={show ? translationKey : ""} profiles={profiles} />
          <ActivityProfilesAvatarStack
            profiles={show ? profiles : []}
            displayChatIcon={!isDraft}
            handleIconClick={startChat}
            avatarSize={30}
            max={6}
            qaId={`ActivitySummary-Profile-${capitalize(translationKey)}`}
            className={css.avatars}
          />
        </Inline>
      ))}
    </Stack>
  );
};
